/**
 * Defensive text limits shared by the browser-facing Elements protocols.
 *
 * These are transport limits, not product-field limits. Opaque identifiers and
 * machine-readable codes are deliberately small; user-facing diagnostics get a
 * larger allowance so localized messages are not truncated. The same values
 * must be kept in sync with the Hosted Checkout protocol validators until the
 * protocol contract is extracted into a shared package.
 */
export const ELEMENTS_PROTOCOL_TEXT_LIMIT = {
  opaqueIdentifierCharacters: 128,
  diagnosticMessageCharacters: 1_024,
} as const;

/**
 * ASCII C0 control characters (U+0000–U+001F) plus DELETE (U+007F).
 *
 * This includes tabs, carriage returns, and line feeds. They are rejected at
 * protocol boundaries so opaque IDs, error codes, and diagnostic messages stay
 * single-line and cannot hide delimiters or create misleading console/log
 * output. Printable Unicode is intentionally allowed; this is not a general
 * Unicode-normalization or content-filtering expression.
 */
const ASCII_CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/u;

export const containsAsciiControlCharacter = (value: string): boolean =>
  ASCII_CONTROL_CHARACTER_PATTERN.test(value);

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Requires the complete object shape, not merely the same number of fields.
 * Protocol validators use this to reject both missing and unexpected fields,
 * keeping messages fail-closed as either side evolves.
 */
export const hasExactKeys = <Key extends string>(
  value: Record<string, unknown>,
  expectedKeys: readonly Key[],
): boolean => {
  const actualKeys = Object.keys(value);
  return (
    actualKeys.every((key) => expectedKeys.includes(key as Key)) &&
    expectedKeys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
};

/**
 * Validates a non-empty, trimmed, single-line protocol string with an explicit
 * maximum size. The caller supplies a named limit so the policy remains visible
 * at each trust boundary.
 */
export const isBoundedProtocolString = (value: unknown, maxCharacters: number): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= maxCharacters &&
  value === value.trim() &&
  !containsAsciiControlCharacter(value);
